<template>
	<!-- Control Panel which includes the buttons that are under the header (top bar) -->
	<div class="control-panel my-2">
    <!-- DIV#1 that contains the buttons on the right side of the control panel -->
    <b-button-toolbar class="d-flex justify-content-md-end">
      <!-- DIV#2 that contains the buttons on the left realisation button, guide icon, and login button -->
      <div class="d-flex my-1">
        <!-- REALISATION BUTTON -->
        <b-button style="z-index:1000" variant="transparent" v-b-tooltip.hover.bottom :title="$t(`realisation.realisation`)">
          <b-icon v-b-modal.realisation-modal icon="info-circle-fill" scale="1.5" variant="white">
          </b-icon>
        </b-button>
        <Realisation />
        <!-- USER GUIDE BUTTON -->
        <b-button style="z-index:1000" @click="toggleIntro()" variant="transparent" v-b-tooltip.hover.bottom :title="$t(`global.user_guide`)">
          <b-icon icon="book" scale="1.5" variant="white"></b-icon>
        </b-button>
        <b-button style="z-index:1000" @click="measureFieldArea()" variant="transparent" v-b-tooltip.hover.bottom :title="$t(`global.measure_area`)">
          <b-icon icon="rulers" scale="1.5" variant="white"></b-icon>
        </b-button>

      </div>
      <!-- END OF DIV#2 -->
    </b-button-toolbar>
  </div>
 </template>
 <script>

import Realisation from './Realisation';
import introJs from 'intro.js';

const GUIDE_FIELD_COORDS = [
  { lat: 33.911670, lng: 36.056760 },
  { lat: 33.912017, lng: 36.057752 },
  { lat: 33.911341, lng: 36.058744 },
  { lat: 33.910797, lng: 36.058036 }
];
const IRRIGATION_AMOUNT_TAB = 0;
const IRRIGATION_HISTORY_TAB = 2;

export default {
  name: 'ControlPanel',

  props: {
    mobileViewPort: { type: Boolean, default: false },
    map: { type: Object },
    user: { type: Object },
  },

	components: {
		Realisation
	},

	data() {
		return {
			activeIntro: null
		}
	},

  beforeDestroy() {
    if (this.activeIntro) {
      this.activeIntro.exit(true).catch(() => undefined);
    }
  },

  methods: {
    clearMap () {
      this.$emit('clearMap')
    },

    measureFieldArea () {
      this.$emit('measureFieldArea');
    },

    async toggleIntro () {
      const vmInstance = this;
      if (!this.map || !this.map.$mapPromise) {
        return;
      }
      if (this.activeIntro) {
        await this.activeIntro.exit(true).catch(() => undefined);
      }

      this.$store.commit('setUserGuideMode', true);
      const intro = introJs();
      this.activeIntro = intro;
      const guideOverlays = [];
      let demoRunId = 0;
      let cleanedUp = false;

      const setElementVisible = (selector, visible) => {
        const element = document.querySelector(selector);
        if (element) {
          element.classList.toggle('d-none', !visible);
        }
      };
      const clearGuideOverlays = () => {
        while (guideOverlays.length) {
          const overlay = guideOverlays.pop();
          if (overlay && typeof overlay.setMap === 'function') {
            overlay.setMap(null);
          }
        }
      };
      const delay = (milliseconds) => new Promise(resolve => setTimeout(resolve, milliseconds));
      const frameGuideField = (map) => {
        const bounds = new window.google.maps.LatLngBounds();
        GUIDE_FIELD_COORDS.forEach(coordinate => bounds.extend(coordinate));

        const mapElement = document.querySelector('.full-screen-map');
        const tooltip = document.querySelector('.introjs-tooltip.guide-draw-tooltip');
        const mapRect = mapElement ? mapElement.getBoundingClientRect() : null;
        const tooltipRect = tooltip ? tooltip.getBoundingClientRect() : null;
        const minimumFieldHeight = 80;
        let topPadding = 24;

        if (mapRect && tooltipRect) {
          const requestedPadding = Math.ceil(tooltipRect.bottom - mapRect.top + 16);
          topPadding = Math.max(24, Math.min(requestedPadding, mapRect.height - minimumFieldHeight));
        }

        map.fitBounds(bounds, {
          top: topPadding,
          right: 20,
          bottom: 20,
          left: 20
        });
      };
      const drawGuideField = async () => {
        const runId = ++demoRunId;
        clearGuideOverlays();
        const map = await vmInstance.map.$mapPromise;

        // Intro.js renders the new step after onbeforechange. Wait for its
        // tooltip dimensions, then reserve that space when framing the field.
        await delay(400);
        if (runId !== demoRunId) return;
        frameGuideField(map);
        await delay(250);

        for (let index = 0; index < GUIDE_FIELD_COORDS.length; index++) {
          if (runId !== demoRunId) return;
          const corner = new window.google.maps.Circle({
            center: GUIDE_FIELD_COORDS[index],
            radius: 2,
            strokeColor: '#000000',
            strokeOpacity: 1,
            strokeWeight: 1,
            fillColor: '#ffffff',
            fillOpacity: 1,
            map
          });
          guideOverlays.push(corner);

          if (index > 0) {
            const edge = new window.google.maps.Polyline({
              path: [GUIDE_FIELD_COORDS[index - 1], GUIDE_FIELD_COORDS[index]],
              geodesic: true,
              strokeColor: '#F57F17',
              strokeOpacity: 1,
              strokeWeight: 2,
              map
            });
            guideOverlays.push(edge);
          }
          await delay(500);
        }

        if (runId !== demoRunId) return;
        const closingEdge = new window.google.maps.Polyline({
          path: [GUIDE_FIELD_COORDS[GUIDE_FIELD_COORDS.length - 1], GUIDE_FIELD_COORDS[0]],
          geodesic: true,
          strokeColor: '#F57F17',
          strokeOpacity: 1,
          strokeWeight: 2,
          map
        });
        guideOverlays.push(closingEdge);
        await delay(500);

        if (runId !== demoRunId) return;
        clearGuideOverlays();
        const completeField = new window.google.maps.Polygon({
          paths: GUIDE_FIELD_COORDS,
          strokeColor: '#F57F17',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#526f2f',
          fillOpacity: 0.35,
          editable: true,
          map
        });
        guideOverlays.push(completeField);
      };

      let steps = [
        {
          title: vmInstance.$t('guide.title_1'),
          intro: vmInstance.$t('guide.step_1')
        },
        {
          title: vmInstance.$t('guide.title_3'),
          element: document.querySelector('#draw-field-button'),
          intro: vmInstance.$t('guide.step_3')
        },
        {
          title: vmInstance.$t('guide.title_4'),
          element: document.querySelector('.full-screen-map'),
          intro: vmInstance.$t('guide.step_4')
        },
        {
            intro: vmInstance.$t('guide.step_click_on_corners'),
            element: document.querySelector('.full-screen-map'),
            title: vmInstance.$t('guide.step_draw_your_field'),
            position: 'floating',
            tooltipClass: 'guide-draw-tooltip'
        },
        {
          title: vmInstance.$t('guide.title_5'),
          element: document.getElementById('guide-save-field-target'),
          intro: vmInstance.$t('guide.step_5')
        },
        {
          title: vmInstance.$t('guide.title_6'),
          element: document.querySelector('.results-window'),
          intro: vmInstance.$t('guide.step_6')
        },
        {
          title: vmInstance.$t('guide.title_7'),
          element: document.querySelector('.results-window'),
          intro: vmInstance.$t('guide.step_7')
        },
      ];

      let guideStepOffset = 0;
      if (!this.user) {
        steps.splice(1, 0, {
          title: vmInstance.$t('guide.title_2'),
          element: document.querySelector('.login-methods'),
          intro: vmInstance.$t('guide.step_2')
        });
        guideStepOffset = 1;
      }

      const drawDemoStep = 3 + guideStepOffset;
      const irrigationAmountStep = 5 + guideStepOffset;
      const irrigationHistoryStep = 6 + guideStepOffset;

      intro.setOptions({
        showStepNumbers: false,
        dontShowAgain: false,
        nextLabel: vmInstance.$t('guide.next'),
        prevLabel: vmInstance.$t('guide.previous'),
        doneLabel: vmInstance.$t('guide.done'),
        steps: steps
      });

      intro.onbeforechange(function() {
        const currentStep = intro.currentStep();
        if (currentStep === drawDemoStep) {
          vmInstance.$emit('goToLocation', 33.911652, 36.057618);
          const overlay = document.querySelector('.introjs-overlay');
          if (overlay) overlay.style.opacity = '0.5';
          drawGuideField().catch(error => {
            console.error('Unable to render the user-guide field demo:', error);
          });
          setElementVisible('.user-guide-button-two', true);
        }

        if (currentStep === irrigationAmountStep) {
          setElementVisible('.user-guide-button-two', false);
          vmInstance.$emit('showGuideResults', IRRIGATION_AMOUNT_TAB);
          vmInstance.$nextTick(() => intro.refresh());
        } else if (currentStep === irrigationHistoryStep) {
          vmInstance.$emit('showGuideResults', IRRIGATION_HISTORY_TAB);
          vmInstance.$nextTick(() => intro.refresh());
        }
      });

      const cleanupGuide = function() {
        if (cleanedUp) return;
        cleanedUp = true;
        demoRunId++;
        clearGuideOverlays();
        setElementVisible('.user-guide-button-two', false);
        vmInstance.$emit('hideGuideResults');
        vmInstance.$emit('goToDetectedLocation');
        vmInstance.$bvModal.hide('new-field-modal');
        vmInstance.$store.commit('setUserGuideMode', false);
        if (vmInstance.activeIntro === intro) {
          vmInstance.activeIntro = null;
        }
      };

      intro.onexit(cleanupGuide);
      intro.oncomplete(cleanupGuide);
      intro.start().catch(error => {
        cleanupGuide();
        console.error('Unable to start the user guide:', error);
      });
    }
  }
}
</script>

<style lang="scss" scoped>
@media (max-width: 768px) { /* adjust the value as needed */
  .mobile-logout {
    display: none;
  }
}


</style>
